<template>
  <div id = 'career-opportunities'>
    <h2>Career Opportunities</h2>
    <p>{{ cip.definition }}</p>
    <p class="disclosure"><i class="fa-solid fa-exclamation-circle"></i> <i>Some occupations associated with this program may require professional licensure for employment. While this program's curriculum may include coursework relevant to licensure, licensure requirements vary by state and profession. To determine whether a specific academic program meets educational requirements for licensure in your state, refer to the UNM Professional Licensure Disclosure Page.</i></p>

    <template v-if = 'occupations.length'>
      <table>
        <tr>
          <th>Occupation</th>
          <th>Median Salary</th>
          <th class = 'salary-cell'>Salary Range</th>
        </tr>
        <tr class = 'salary-row'>
          <td></td>
          <td></td>
          <td class = 'salary-cell'>
            <div class = 'salary-scale'>
              <div class = 'salary-scale-min salary-scale-label'>{{ dollars(salaryMin) }}</div>
              <div class = 'salary-scale-mid salary-scale-label'>{{ dollars(salaryMid) }}</div>
              <div class = 'salary-scale-max salary-scale-label'>{{ dollars(salaryMax) }}</div>
              <div class = 'salary-scale-domain'></div>
            </div>
          </td>
        </tr>
        <tr
          is = 'occupation'
          v-for = 'occupation in occupations'
          v-if = 'occupation.salary && occupation.tags && occupation.salary.annual_median'
          :occupation = 'occupation'
          :salary-min = 'salaryMin'
          :salary-range = 'salaryRange'
          :key="occupation.code"
        >
        </tr>
      </table>
      <p class = 'note-everyday'>
        Career data obtained from the Occupational Information Network (O*NET).
        These occupations are identified based on the CIP code for {{ cip.name }}
        ({{ cip.code }}). The salary range depicts the 10th percentile, median, and
        90th percentile salary values for each occupation.
      </p>
      <div class = 'job-legend'>
        <template v-if = 'showGreenLegend'>
          <div class = 'job-legend-header'>
            <span class = 'job-legend-icon'><img src = '../assets/images/green_job.png' height = '24'></span>
            <span class = 'job-legend-label'>Green Occupation</span>
          </div>
          <p>
            Green occupations will likely change as a result of the green economy.
            Green economy activities and technologies are increasing the demand for
            occupations, shaping the work and worker requirements needed for
            occupational performance, or generating new and emerging occupations.
          </p>
        </template>

        <template v-if = 'showBrightOutlookLegend'>
          <div class = 'job-legend-header'>
            <span class = 'job-legend-icon'><img src = '../assets/images/bright_outlook_job.png' height = '24'></span>
            <span class = 'job-legend-label'>Bright Outlook</span>
          </div>
          <p>
            Bright Outlook occupations are expected to grow rapidly in the next
            several years, will have large numbers of job openings, or are new and
            emerging occupations.
          </p>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import {get} from 'axios'
import {format} from 'd3-format'
import {min, max} from 'd3'
import {some} from 'lodash'
import Occupation from './occupation'

const defaultCareerData = { occupations: [] }

export default {
  name: 'CareerOpportunities',

  data() {
    return {
      dollars: format('$.2s'),
      careerData: defaultCareerData
    }
  },

  created() {
    this.fetchData()
  },

  watch: {
    '$route': 'fetchData'
  },

  props: {
    cip: {
      required: true,
      type: Object
    }
  },

  computed: {
    salaryMin() {
      return min(this.occupations, o => o.salary.annual_10th_percentile)
    },

    salaryMax() {
      return max(this.occupations, o => o.salary.annual_90th_percentile || o.salary.annual_90th_percentile_over)
    },

    salaryMid() {
      return (this.salaryMin + this.salaryMax) / 2
    },

    salaryRange() {
      return this.salaryMax - this.salaryMin
    },

    occupations() {
      return this.careerData.occupations.filter(o => o.salary)
    },

    showGreenLegend() {
      return some(this.careerData.occupations, o => o.tags.green)
    },

    showBrightOutlookLegend() {
      return some(this.careerData.occupations, o => o.tags.bright_outlook)
    }
  },

  methods: {
    fetchData() {
      this.careerData = defaultCareerData
      get('https://careers.nmrpsp.org/cips/' + this.cip.code)
        .then(response => { this.careerData = response.data || defaultCareerData })
    }
  },

  components: {
    Occupation
  }
}

</script>

<style scoped lang="scss">
.disclosure {
  background-color: rgba(orange, 0.1);
  font-size: 13px;
  padding: 10px;
  border-radius: 4px;
}
</style>



// WEBPACK FOOTER //
// career_opportunities.vue?46e1cd09